import React from 'react';
import {
  Controller,
  Control,
  FieldError,
  FieldValues,
  Path,
} from 'react-hook-form';
import TooltipErrors, { TooltipProps } from '../TooltipErrors/TooltipErrors';
import ErrorIcon from '../../assets/icons/ErrorIcon';
import dayjs, { Dayjs } from 'dayjs';
import { formatDateForDisplay } from '../../utils/dateFormat';
import { SxProps, Theme } from '@mui/material';
import { errorsProps, warningsProps } from '../SummaryErrors/SummaryErrors';
import { getFieldErrors } from '../../utils/getFieldError';
import { removeOtherErrorsIfThereIsFieldRequiredError } from '../../utils/filterFieldErrors';
import WarningIcon from '../../assets/icons/Warning';
import RadioLabel from '../RadioLabel/RadioLabel';
import DateTimePicker from '../DateTimePicker/DateTimePicker';

interface ControlledFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T> | string;
  component: React.ReactElement;
  extractErrors?: (error: FieldError | undefined) => string[];
  tooltipPlacement?: TooltipProps;
  tooltipSx?: SxProps<Theme>;
  sx?: SxProps;
  fullWidth?: boolean;
  required?: boolean;
  type?: string;
  endAdornment?: React.ReactElement;
  autocompleteHtmlAttribute?: string;
  onChangeCompleted?: (value: unknown) => unknown;
  onBlurCompleted?: (value: unknown) => unknown;
  backendErrors?: errorsProps[];
  warnings?: warningsProps[];
  storeValue?: boolean;
}

const extractErrorMessages = (fieldError: FieldError | undefined): string[] => {
  if (!fieldError) return [];

  const error = removeOtherErrorsIfThereIsFieldRequiredError(fieldError);

  const messages = new Set<string>();

  if (error.message) messages.add(error.message);

  if (error.types) {
    Object.values(error.types).forEach((msg) => {
      if (Array.isArray(msg)) {
        msg.forEach((m) => messages.add(m));
      } else {
        messages.add(msg as string);
      }
    });
  }

  if (Array.isArray(error)) {
    error.forEach((e) => messages.add(e.message));
  }

  return Array.from(messages);
};

function ControlledField<T extends FieldValues>({
  name,
  control,
  component,
  tooltipPlacement,
  tooltipSx,
  onChangeCompleted,
  onBlurCompleted,
  backendErrors,
  warnings,
  storeValue,
}: ControlledFieldProps<T>) {
  return (
    <Controller
      name={name as Path<T>}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const backendErrorsForField = getFieldErrors(backendErrors || [], name);
        const frontendErrors = extractErrorMessages(error);
        const warningMessages =
          warnings?.find((w) => w.fieldName === name)?.fieldWarnings || [];

        const summaryType: 'error' | 'warning' =
          frontendErrors?.length > 0 || backendErrorsForField?.length > 0
            ? 'error'
            : 'warning';

        const errors =
          frontendErrors.length > 0 ? frontendErrors : backendErrorsForField;
        const isDateField =
          component.type === DateTimePicker || Boolean(component.props?.date);
        const isCheckbox = component.type === RadioLabel;

        const computeFieldValue = (): any => {
          const baseValue = isDateField
            ? field.value
              ? dayjs(field.value)
              : null
            : field.value;

          if (
            component.props.multiple &&
            Array.isArray(baseValue) &&
            component.props.options
          ) {
            return baseValue
              .map((label: string) =>
                component.props.options.find((opt: any) =>
                  typeof opt === 'string' ? opt === label : opt.label === label
                )
              )
              .filter(Boolean);
          }

          if (!component.props.multiple && component.props.options) {
            if (
              storeValue &&
              (typeof baseValue === 'number' || typeof baseValue === 'string')
            ) {
              const match = component.props.options.find((opt: any) =>
                typeof opt === 'string'
                  ? opt === baseValue
                  : opt.value === baseValue
              );
              return match ?? null;
            }

            if (typeof baseValue === 'string') {
              const match = component.props.options.find((opt: any) =>
                typeof opt === 'string'
                  ? opt === baseValue
                  : opt.label === baseValue
              );
              return match ?? null;
            }
          }

          return baseValue;
        };

        const fieldValue = computeFieldValue();
        const isTextInput =
          !isDateField &&
          !isCheckbox &&
          !component.props.multiple &&
          !component.props.options;
        const emptyValue = component.props.multiple
          ? []
          : isTextInput
            ? ''
            : null;

        return React.cloneElement(component, {
          ...field,
          value: fieldValue ?? emptyValue,
          onChange: (value: Dayjs | string | { value: unknown } | null) => {
            if (
              isDateField &&
              (value === null ||
                typeof value === 'string' ||
                dayjs.isDayjs(value))
            ) {
              field.onChange(formatDateForDisplay(value));
              onChangeCompleted &&
                onChangeCompleted(formatDateForDisplay(value));
            } else if (isCheckbox) {
              field.onChange((value as any).target.checked);
              onChangeCompleted &&
                onChangeCompleted((value as any).target.checked);
            } else if (Array.isArray(value)) {
              const labels = value.map((item) =>
                typeof item === 'object' && item !== null && 'label' in item
                  ? item.label
                  : item
              );
              field.onChange(labels);
              onChangeCompleted && onChangeCompleted(labels);
            } else if (
              value &&
              typeof value === 'object' &&
              'value' in value &&
              'label' in value
            ) {
              if (storeValue) {
                field.onChange(value.value);
                onChangeCompleted && onChangeCompleted(value.value);
              } else {
                field.onChange(value.label);
                onChangeCompleted && onChangeCompleted(value.label);
              }
            } else {
              const normalizedValue = storeValue
                ? (value ?? null)
                : (value ?? '');
              field.onChange(normalizedValue);
              onChangeCompleted && onChangeCompleted(normalizedValue);
            }
          },
          onBlur: () => {
            field.onBlur();
            onBlurCompleted && onBlurCompleted(fieldValue);
          },
          error: Boolean(errors.length > 0),
          warning: Boolean(warningMessages.length > 0),
          errors,
          tooltip: (
            <TooltipErrors
              errors={errors}
              warnings={warningMessages}
              tooltipPlacement={tooltipPlacement}
              tooltipSx={tooltipSx}
              type={summaryType}
            >
              {summaryType === 'error' ? <ErrorIcon /> : <WarningIcon />}
            </TooltipErrors>
          ),
        });
      }}
    />
  );
}

export default ControlledField;
